/**
 * CockpitRuntime - Main entry point for the Intelligence Cockpit
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { CIG, CompiledViews, Persona, DEFAULT_PERSONA, TabId, ScenarioAxis, Source } from '../../lib/cockpit/schemas'
import { ScenarioProvider } from './scenario/ScenarioContext'
import { LoadingAnimation } from '../ui/LoadingAnimation'
import { saveResearchArticle, unsaveResearchArticle, saveIntelligenceReport, unsaveIntelligenceReport, generateIntelligenceReportImage, fetchIntelligenceReportImage, API_BASE_URL } from '../../lib/api'
import { resolveAssetUrl } from '../../lib/urlFormatting'
import { buildReportPdfModel } from '../../lib/pdfExport'
import { downloadReportPdf, resolvePdfImageUrl } from '../../lib/reportPdf'
import { buildCanonicalShareUrl } from '../../lib/shareUrls'
import { analytics, useScreenEngagement } from '../../lib/analytics'
import Avatar from '../ui/Avatar'
import { getConsistentAvatarUrl } from '../../hooks/useUserAvatar'
import ReportCoverImage from '../shared/ReportCoverImage'
import ShareSheet from '../shared/ShareSheet'

// Direct imports instead of lazy loading for faster tab switching
import CockpitTab from './tabs/CockpitTab'
import WrittenReportTab from './tabs/WrittenReportTab'
import DataFiguresTab from './tabs/DataFiguresTab'
import ConnectionsTab from './tabs/ConnectionsTab'
import SourcesMethodsTab from './tabs/SourcesMethodsTab'

type RuntimeTabId = TabId | 'info'

/**
 * Find the closest scrollable parent element
 */
function findScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null
  
  let current: HTMLElement | null = element.parentElement
  while (current) {
    const { overflow, overflowY } = window.getComputedStyle(current)
    const isScrollable = overflow === 'auto' || overflow === 'scroll' || 
                         overflowY === 'auto' || overflowY === 'scroll'
    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current
    }
    current = current.parentElement
  }
  return null
}

const TAB_ICONS: Record<RuntimeTabId, React.ReactNode> = {
  cockpit: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>,
  written_report: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  takeaways: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  data_figures: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  connections: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
  sources_methods: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>,
  info: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 6a9 9 0 100 18 9 9 0 000-18z" /></svg>,
}

const TAB_LABELS: Record<RuntimeTabId, string> = {
  cockpit: 'Executive Overview',
  written_report: 'Written Report',
  takeaways: 'Takeaways',
  data_figures: 'Data & Figures',
  connections: 'Connections',
  sources_methods: 'Sources & Methods',
  info: 'Info',
}

type StudyMeta = {
  id?: string
  intelligenceReportId?: string
  researchId?: number  // Numeric ID for Save/Share APIs
  isSaved?: boolean
  isPublic?: boolean
  category?: string
  model?: string
  createdAt?: string
  completedAt?: string
  query?: string
  researcher?: string
  researcherEmail?: string
  researcherAvatarUrl?: string
}

export interface CockpitRuntimeProps {
  cig: CIG
  views: CompiledViews
  persona?: Persona
  initialTab?: TabId
  onBack?: () => void
  onExport?: (format: string) => void
  onListen?: () => void | Promise<void>
  listenLabel?: string
  listenDisabled?: boolean
  listenLoading?: boolean
  viewMode?: 'mobile' | 'tablet' | 'desktop'
  showHeader?: boolean
  hasFloatingAudio?: boolean
  studyMeta?: StudyMeta
  heroImageUrl?: string
  onRequestDownload?: () => void
  onPrivacyToggle?: (isPublic: boolean) => Promise<boolean>
  onNavigateToProfile?: (email: string) => void
}

function TabSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
  )
}

export function CockpitRuntime({
  cig,
  views,
  persona = DEFAULT_PERSONA,
  initialTab = 'cockpit',
  onBack,
  onExport,
  onListen,
  listenLabel = 'Play Podcast',
  listenDisabled = false,
  listenLoading = false,
  viewMode = 'desktop',
  showHeader = true,
  hasFloatingAudio = false,
  studyMeta,
  heroImageUrl,
  onRequestDownload,
  onPrivacyToggle,
  onNavigateToProfile,
}: CockpitRuntimeProps) {
  const [activeTab, setActiveTab] = useState<RuntimeTabId>(initialTab)
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(studyMeta?.isSaved ?? false)
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false)
  const [dynamicImageUrl, setDynamicImageUrl] = useState<string | null>(null)
  const tabRefs = useRef<Map<RuntimeTabId, HTMLButtonElement>>(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })
  const containerRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const tabContentRef = useRef<HTMLElement>(null)

  const scrollRuntimeToTop = useCallback((reason: string) => {
    const scrollableParent = findScrollableParent(containerRef.current)
    const isTabClick = reason.startsWith('tab-click:')
    if (isTabClick && scrollableParent && navRef.current && tabContentRef.current) {
      const navRect = navRef.current.getBoundingClientRect()
      const contentRect = tabContentRef.current.getBoundingClientRect()
      const desiredContentTop = navRect.bottom
      const scrollDelta = contentRect.top - desiredContentTop
      const targetScrollTop = Math.max(0, scrollableParent.scrollTop + scrollDelta)

      scrollableParent.scrollTop = targetScrollTop
      return
    }
    if (scrollableParent) {
      scrollableParent.scrollTop = 0
    }
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [])

  useEffect(() => {
    setIsSaved(studyMeta?.isSaved ?? false)
  }, [studyMeta?.isSaved, studyMeta?.id])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      scrollRuntimeToTop('report-mount')
    }, 60)

    return () => {
      window.clearTimeout(timer)
    }
  }, [scrollRuntimeToTop, studyMeta?.id, studyMeta?.intelligenceReportId])

  // Check if this is an intelligence report
  const isIntelligenceReport = useMemo(() => {
    return Boolean(studyMeta?.intelligenceReportId)
  }, [studyMeta?.intelligenceReportId])

  // Get the intelligence report ID for intelligence-specific API calls
  const intelligenceReportId = useMemo(() => {
    return studyMeta?.intelligenceReportId || null
  }, [studyMeta?.intelligenceReportId])

  // Extract numeric research ID for Save/Share functionality
  // For intelligence reports, use researchId from studyMeta
  // For regular research, parse the id
  // Note: researchId of 0 is invalid (indicates no linked research_studies entry)
  const researchId = useMemo(() => {
    // If we have a valid positive researchId from the backend, use it
    if (typeof studyMeta?.researchId === 'number' && studyMeta.researchId > 0) {
      return studyMeta.researchId
    }
    
    // Otherwise parse from the id (for regular research)
    const rawId = studyMeta?.id
    if (!rawId) return null
    
    // Don't try to parse ia-{uuid} format
    if (String(rawId).startsWith('ia-')) {
      return null
    }
    
    const parsed = parseInt(String(rawId), 10)
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed
  }, [studyMeta?.id, studyMeta?.researchId])

  const analyticsResearchId = useMemo(() => {
    if (researchId) return String(researchId)
    if (studyMeta?.id) return String(studyMeta.id)
    if (intelligenceReportId) return intelligenceReportId
    return 'unknown'
  }, [intelligenceReportId, researchId, studyMeta?.id])

  const {
    trackFullReportExpanded,
    trackPodcastPlayed,
    trackSaved,
    trackShared,
    trackSourceClicked,
    trackTabViewed,
    trackUnsaved,
  } = useScreenEngagement({
    screenName: 'article',
    researchId: analyticsResearchId !== 'unknown' ? analyticsResearchId : undefined,
  })
  const fullReportOpenedAtRef = useRef<number | null>(null)
  const fullReportMaxScrollDepthRef = useRef(0)
  const fullReportSourceClicksRef = useRef(0)
  const fullReportTrackedRef = useRef(false)

  useEffect(() => {
    fullReportOpenedAtRef.current = null
    fullReportMaxScrollDepthRef.current = 0
    fullReportSourceClicksRef.current = 0
    fullReportTrackedRef.current = false
  }, [analyticsResearchId, intelligenceReportId])

  // Get AI-generated title (priority: written_report.title from SIB research_title, then CIG metadata, then BLUF, then query)
  const displayTitle = useMemo(() => {
    const cigTitle = cig.metadata?.title
    const reportTitle = views.written_report?.title
    const bluf = cig.bluf
    const query = studyMeta?.query
    
    // Prefer report title (SIB research_title) — reject mission statements (>150 chars)
    if (reportTitle && reportTitle.trim().length > 10 && reportTitle.length < 150 && reportTitle !== query) return reportTitle
    if (cigTitle && cigTitle !== query && cigTitle.length > 10 && cigTitle.length < 150) return cigTitle
    // Do NOT fall back to user query — generate a title from BLUF if available
    if (bluf && bluf.length > 20 && bluf.length < 150 && bluf !== query) return bluf
    // Last resort: use the query but it should rarely reach here for new reports
    return query || 'Research Report'
  }, [cig.metadata?.title, views.written_report?.title, cig.bluf, studyMeta?.query])

  const shareUrl = useMemo(() => {
    return buildCanonicalShareUrl({
      researchId,
      intelligenceReportId,
    })
  }, [intelligenceReportId, researchId])

  const shareText = useMemo(() => {
    return `I thought you might find this interesting: "${displayTitle || 'Research Report'}"`
  }, [displayTitle])

  const shareResourceLabel = intelligenceReportId ? 'report' : 'article'

  const resolvedHeroImage = useMemo(() => {
    // Use dynamically generated image if available
    if (dynamicImageUrl) return dynamicImageUrl
    if (!heroImageUrl) return undefined
    try {
      return resolveAssetUrl(heroImageUrl, API_BASE_URL)
    } catch {
      return heroImageUrl
    }
  }, [heroImageUrl, dynamicImageUrl])

  useEffect(() => {
    if (!intelligenceReportId || heroImageUrl || dynamicImageUrl) return
    let isMounted = true
    fetchIntelligenceReportImage(intelligenceReportId).then((imageData) => {
      if (isMounted && imageData) {
        setDynamicImageUrl(imageData)
      }
    })
    return () => {
      isMounted = false
    }
  }, [intelligenceReportId, heroImageUrl, dynamicImageUrl])

  // Handle generate image for intelligence reports
  const handleGenerateImage = useCallback(async () => {
    if (!intelligenceReportId || isGeneratingImage) return
    
    setIsGeneratingImage(true)
    try {
      const imageData = await generateIntelligenceReportImage(intelligenceReportId)
      if (imageData) {
        setDynamicImageUrl(imageData)
        analytics.trackContentInteraction('article', 'cover_image_generated', 'button', intelligenceReportId)
      } else {
        alert('Failed to generate image. Please try again.')
      }
    } catch (error) {
      console.error('[CockpitRuntime] Generate image error:', error)
      alert('Failed to generate image. Please try again.')
    } finally {
      setIsGeneratingImage(false)
    }
  }, [intelligenceReportId, isGeneratingImage])

  // Handle Save - uses appropriate API based on report type
  // Intelligence reports use saveIntelligenceReport/unsaveIntelligenceReport with string reportId
  // Regular research uses saveResearchArticle/unsaveResearchArticle with numeric researchId
  const handleSave = useCallback(async () => {
    if (isSaving) return
    
    // For intelligence reports, use intelligence report APIs with string ID
    if (isIntelligenceReport && intelligenceReportId) {
      setIsSaving(true)
      try {
        const success = isSaved
          ? await unsaveIntelligenceReport(intelligenceReportId)
          : await saveIntelligenceReport(intelligenceReportId)
        
        if (success) {
          setIsSaved(prev => !prev)
          if (isSaved) {
            trackUnsaved()
            if (analyticsResearchId !== 'unknown') {
              analytics.trackArticleUnsaved(analyticsResearchId, intelligenceReportId || undefined)
            }
          } else {
            trackSaved()
            if (analyticsResearchId !== 'unknown') {
              analytics.trackArticleSaved({
                research_id: analyticsResearchId,
                report_id: intelligenceReportId || undefined,
                source: 'global',
                category: studyMeta?.category,
              })
            }
          }
        } else {
          alert('Failed to save. Please try again.')
        }
      } catch (error) {
        console.error('[CockpitRuntime] Intelligence report save error:', error)
        alert('Failed to save. Please try again.')
      } finally {
        setIsSaving(false)
      }
      return
    }
    
    // For regular research, use research article APIs with numeric ID
    if (!researchId) {
      console.error('[CockpitRuntime] No valid ID for save', { studyMeta, researchId, intelligenceReportId })
      alert('Unable to save: Research not yet indexed. Please try again later.')
      return
    }
    
    setIsSaving(true)
    try {
      const success = isSaved
        ? await unsaveResearchArticle(researchId)
        : await saveResearchArticle(researchId)
      
      if (success) {
        setIsSaved(prev => !prev)
        if (isSaved) {
          trackUnsaved()
          if (analyticsResearchId !== 'unknown') {
            analytics.trackArticleUnsaved(analyticsResearchId, intelligenceReportId || undefined)
          }
        } else {
          trackSaved()
          if (analyticsResearchId !== 'unknown') {
            analytics.trackArticleSaved({
              research_id: analyticsResearchId,
              report_id: intelligenceReportId || undefined,
              source: 'global',
              category: studyMeta?.category,
            })
          }
        }
      } else {
        alert('Failed to save. Please try again.')
      }
    } catch (error) {
      console.error('[CockpitRuntime] Save error:', error)
      alert('Failed to save. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }, [researchId, isSaved, isSaving, studyMeta, isIntelligenceReport, intelligenceReportId, analyticsResearchId, trackSaved, trackUnsaved])

  const handleShare = useCallback(() => {
    if (!shareUrl) {
      alert('Unable to share this report yet.')
      return
    }

    analytics.trackModalOpened({
      modal_type: 'share',
      trigger: 'cockpit',
    })
    setIsShareSheetOpen(true)
  }, [shareUrl])

  const flushFullReportRead = useCallback(() => {
    const openedAt = fullReportOpenedAtRef.current
    if (!openedAt || fullReportTrackedRef.current || analyticsResearchId === 'unknown') {
      return
    }

    analytics.trackFullReportRead({
      research_id: analyticsResearchId,
      report_id: intelligenceReportId || undefined,
      read_duration_seconds: Math.max(1, Math.round((Date.now() - openedAt) / 1000)),
      scroll_depth_percent: Math.max(1, Math.round(fullReportMaxScrollDepthRef.current)),
      sources_clicked: fullReportSourceClicksRef.current,
    })
    fullReportTrackedRef.current = true
    fullReportOpenedAtRef.current = null
  }, [analyticsResearchId, intelligenceReportId])

  const updateFullReportScrollDepth = useCallback(() => {
    if (activeTab !== 'written_report') return

    const contentElement = tabContentRef.current
    if (!contentElement) return

    const scrollableParent = findScrollableParent(containerRef.current)
    const contentRect = contentElement.getBoundingClientRect()
    const viewportBottom = scrollableParent
      ? scrollableParent.getBoundingClientRect().bottom
      : window.innerHeight
    const visibleBottomWithinContent = Math.min(
      contentRect.height,
      Math.max(0, viewportBottom - contentRect.top)
    )
    if (contentRect.height <= 0 || visibleBottomWithinContent <= 0) return

    fullReportMaxScrollDepthRef.current = Math.max(
      fullReportMaxScrollDepthRef.current,
      Math.min(100, (visibleBottomWithinContent / contentRect.height) * 100)
    )
  }, [activeTab])

  const handleContentClickCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement | null
    const anchor = target?.closest('a[href]')
    if (!anchor) return

    const href = anchor.getAttribute('href')
    if (!href) return

    try {
      const parsedUrl = new URL(href, window.location.origin)
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) return
      if (parsedUrl.origin === window.location.origin) return

      const sourceIndex = fullReportSourceClicksRef.current + 1
      fullReportSourceClicksRef.current = sourceIndex
      trackSourceClicked()

      if (analyticsResearchId !== 'unknown') {
        analytics.trackSourceClicked({
          research_id: analyticsResearchId,
          report_id: intelligenceReportId || undefined,
          source_index: sourceIndex,
          source_domain: parsedUrl.hostname.replace(/^www\./, ''),
        })
      }
    } catch {
      // Ignore malformed URLs and non-navigational anchors.
    }
  }, [analyticsResearchId, intelligenceReportId, trackSourceClicked])

  const handleListenClick = useCallback(async () => {
    if (!onListen) return

    trackPodcastPlayed()
    if (analyticsResearchId !== 'unknown') {
      analytics.trackPodcastPlayed({
        research_id: analyticsResearchId,
        report_id: intelligenceReportId || undefined,
        duration_listened_seconds: 0,
        completion_rate_percent: 0,
        source: 'article',
      })
    }

    await onListen()
  }, [analyticsResearchId, intelligenceReportId, onListen, trackPodcastPlayed])

  const handleShareAction = useCallback((method: 'copy_link' | 'native_share') => {
    trackShared()
    if (analyticsResearchId === 'unknown') return

    analytics.trackArticleShared({
      research_id: analyticsResearchId,
      report_id: intelligenceReportId || undefined,
      share_method: method,
      is_own_research: false,
    })
  }, [analyticsResearchId, intelligenceReportId, trackShared])

  // Handle Download PDF
  const handleDownloadPdf = useCallback(async () => {
    if (isDownloadingPdf) return

    const reportMarkdown = views.written_report?.deep_research_markdown ||
      views.written_report?.fallback_markdown ||
      cig.bluf ||
      'No report content available.'

    const publishedDate = new Date(studyMeta?.completedAt || studyMeta?.createdAt || Date.now()).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })

    setIsDownloadingPdf(true)
    try {
      const pdfCoverImageUrl = resolvedHeroImage && /\/research\/\d+\/image\/?$/.test(resolvedHeroImage)
        ? await resolvePdfImageUrl(resolvedHeroImage)
        : resolvedHeroImage

      await downloadReportPdf(
        buildReportPdfModel({
          title: displayTitle,
          coverImageUrl: pdfCoverImageUrl,
          category: studyMeta?.category,
          generatedLabel: `Published ${publishedDate}`,
          preparedBy: studyMeta?.researcher ? `Prepared by ${studyMeta.researcher}` : undefined,
          reportMarkdown,
          excludeTocTitles: ['Executive Summary', 'Key Findings', 'References'],
          tocMaxDepth: 2,
          tocMaxEntries: 10,
        }),
      )
      if (analyticsResearchId !== 'unknown') {
        analytics.trackContentInteraction('article', 'pdf_downloaded', 'button', intelligenceReportId || analyticsResearchId)
      }
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again.')
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [views.written_report, displayTitle, cig.bluf, resolvedHeroImage, studyMeta, isDownloadingPdf])

  // Update tab indicator position (accounts for scroll position)
  useEffect(() => {
    const updateIndicator = () => {
      const activeButton = tabRefs.current.get(activeTab)
      const nav = navRef.current
      if (activeButton && nav) {
        // Use offsetLeft which is relative to the parent, not affected by scroll
        setIndicatorStyle({
          left: activeButton.offsetLeft,
          width: activeButton.offsetWidth,
        })

        // Auto-scroll the active tab into view.
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    }
    
    // Run immediately and after a short delay (for initial render)
    updateIndicator()
    const timer = setTimeout(updateIndicator, 100)
    return () => clearTimeout(timer)
  }, [activeTab])

  // Prefetch tabs
  useEffect(() => {
    if (viewMode === 'mobile') return
      import('./tabs/WrittenReportTab')
      import('./tabs/DataFiguresTab')
      import('./tabs/ConnectionsTab')
      import('./tabs/SourcesMethodsTab')
  }, [viewMode])

  useEffect(() => {
    const scrollableParent = findScrollableParent(containerRef.current)
    const scrollTarget: HTMLElement | Window = scrollableParent || window
    const handleScroll = () => updateFullReportScrollDepth()

    handleScroll()
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [updateFullReportScrollDepth])

  useEffect(() => {
    trackTabViewed(activeTab)

    if (activeTab === 'written_report') {
      if (!fullReportOpenedAtRef.current) {
        fullReportOpenedAtRef.current = Date.now()
        trackFullReportExpanded()
      }
      updateFullReportScrollDepth()
      return
    }

    flushFullReportRead()
  }, [activeTab, flushFullReportRead, trackFullReportExpanded, trackTabViewed, updateFullReportScrollDepth])

  useEffect(() => {
    return () => {
      flushFullReportRead()
    }
  }, [flushFullReportRead])
  
  const [evidenceDrawerOpen, setEvidenceDrawerOpen] = useState(false)
  const [evidenceBlockId, setEvidenceBlockId] = useState<string | null>(null)
  const scenarioAxis = cig.scenario_axes?.[0] ?? null

  const normalizedSources = useMemo<Source[]>(() => {
    const rawSources = views.sources_methods?.sources || []
    return rawSources.map((source: any) => ({
        id: source.id,
        title: source.title,
        publisher: source.publisher,
        authors: source.authors,
        date: source.date,
        url: source.url,
        source_type: source.source_type,
        reliability: {
        score: source.reliability_score ?? source.reliability?.score ?? 0.8,
        rationale: source.reliability_rationale ?? source.reliability?.rationale ?? '',
          tier: source.reliability_tier ?? source.reliability?.tier,
        },
        excerpts: source.excerpts,
    } as Source))
  }, [views.sources_methods?.sources])

  const orderedTabs = useMemo(() => {
    const tabOrder = persona.tab_ordering || views.tabs.map((t: any) => t.id)
    const hiddenTabs = new Set(persona.hidden_tabs || [])
    return tabOrder.filter((tabId: any) => 
      !hiddenTabs.has(tabId) && 
      views.tabs.some((t: any) => t.id === tabId && t.enabled)
    )
  }, [persona, views.tabs])

  const runtimeTabs: RuntimeTabId[] = useMemo(() => [...orderedTabs, 'info'], [orderedTabs])

  const handleOpenEvidence = useCallback((blockId: string) => {
    setEvidenceBlockId(blockId)
    setEvidenceDrawerOpen(true)
  }, [])

  const renderTabContent = () => {
    switch (activeTab) {
      case 'cockpit':
        return <CockpitTab view={views.cockpit} cig={cig} writtenReportView={views.written_report} onOpenEvidence={handleOpenEvidence} onSwitchTab={setActiveTab} viewMode={viewMode} sourceList={normalizedSources} />
      case 'written_report':
        return <WrittenReportTab view={views.written_report} cig={cig} onOpenEvidence={handleOpenEvidence} viewMode={viewMode} />
      case 'data_figures':
        return <DataFiguresTab view={views.data_figures} cig={cig} viewMode={viewMode} />
      case 'connections':
        return <ConnectionsTab view={views.connections} cig={cig} viewMode={viewMode} />
      case 'sources_methods':
        return <SourcesMethodsTab view={views.sources_methods} cig={cig} viewMode={viewMode} />
      case 'info':
        return <InfoPanel cig={cig} studyMeta={studyMeta} onPrivacyToggle={onPrivacyToggle} onNavigateToProfile={onNavigateToProfile} />
      default:
        return <div className="p-6 text-center text-gray-500">Tab not implemented: {activeTab}</div>
    }
  }

  const isMobile = viewMode === 'mobile'
  const publishedDate = studyMeta?.completedAt || studyMeta?.createdAt
  const formattedDate = publishedDate 
    ? (() => {
        const d = new Date(publishedDate)
        const month = d.toLocaleDateString('en-US', { month: 'short' })
        const day = d.getDate()
        const year = d.getFullYear()
        return `${month}. ${day}, ${year}`
      })()
    : null
  const actionLayoutClass = onListen
    ? 'mt-4 grid grid-cols-2 gap-2 sm:mt-5 sm:flex sm:flex-wrap sm:items-center sm:gap-3'
    : 'mt-4 grid grid-cols-3 gap-2 sm:mt-5 sm:flex sm:flex-wrap sm:items-center sm:gap-3'
  const primaryActionClass = 'flex min-h-[52px] w-full min-w-0 items-center justify-center gap-2 rounded-2xl px-3 py-3 text-sm font-semibold shadow-sm transition-all active:scale-[0.98] sm:inline-flex sm:min-h-0 sm:w-auto sm:min-w-[90px] sm:rounded-xl sm:px-4 sm:py-2.5 sm:font-medium sm:active:scale-95'
  const secondaryActionClass = 'flex min-h-[52px] w-full min-w-0 items-center justify-center gap-2 rounded-2xl border px-3 py-3 text-sm font-semibold shadow-sm transition-all active:scale-[0.98] sm:inline-flex sm:min-h-0 sm:w-auto sm:min-w-[90px] sm:rounded-xl sm:px-4 sm:py-2.5 sm:font-medium sm:shadow-none sm:active:scale-95'
  const mobileSecondaryActionClass = 'flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-medium text-gray-700 transition-all active:scale-[0.98] dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-300'
  const mobileSaveToggleClass = `
    inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border transition-all active:scale-[0.98]
    ${isSaved
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400'
      : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
    }
    ${isSaving ? 'opacity-60' : ''}
  `
  const mobileTabContentPaddingClass = hasFloatingAudio ? 'pb-[10.5rem]' : 'pb-[6.75rem]'
  const saveButtonLabel = isSaving ? 'Saving' : isSaved ? 'Saved' : 'Save'

  return (
    <ScenarioProvider axis={scenarioAxis} personaDefaultState={persona.default_scenario_state}>
      <div ref={containerRef} className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Hero Section - Full width on mobile, contained on desktop */}
        {showHeader && (
          <div className="w-full px-3 sm:px-6 pt-4 sm:pt-6 md:max-w-[1600px] md:mx-auto">
            {/* Back Button - Larger touch target on mobile */}
            {onBack && (
              <button
                onClick={onBack}
                className="mb-3 sm:mb-4 inline-flex items-center gap-2 text-base sm:text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors py-2.5 sm:py-2 -ml-2 px-3 sm:px-2 rounded-xl active:bg-gray-100 dark:active:bg-gray-800"
              >
                <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
            )}

            {/* Hero Card - Contains Image, Title, Date, and Buttons */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              {/* Hero Image */}
              <div className="relative bg-gray-900">
                {resolvedHeroImage ? (
                  <ReportCoverImage
                    src={resolvedHeroImage}
                    alt={`${displayTitle} cover art`}
                  />
                ) : (
                  <div className="flex min-h-[240px] items-center justify-center bg-gradient-to-br from-gray-800 via-gray-900 to-black md:min-h-[320px]">
                    <div className="text-center text-white/50">
                      <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">Insight Atlas</span>
                      {intelligenceReportId && (
                        <button
                          onClick={handleGenerateImage}
                          disabled={isGeneratingImage}
                          className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all disabled:opacity-50"
                        >
                          {isGeneratingImage ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Generating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Generate Cover Image
                            </>
                          )}
                    </button>
                  )}
                    </div>
                  </div>
                )}
                </div>

              {/* Content Section */}
              <div className="p-4 sm:p-6">
                {/* Title */}
                <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                  {displayTitle}
                </h1>

                {isMobile ? (
                  <>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        {formattedDate && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Published {formattedDate}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        aria-label={saveButtonLabel}
                        aria-pressed={isSaved}
                        className={mobileSaveToggleClass}
                        title={saveButtonLabel}
                      >
                        {isSaving ? <LoadingSpinnerIcon /> : <BookmarkIcon filled={isSaved} />}
                      </button>
                    </div>

                    <div className="mt-4 space-y-2.5">
                      {onListen && (
                        <button
                          onClick={() => { void handleListenClick() }}
                          disabled={listenDisabled}
                          className={`${primaryActionClass} border border-transparent bg-gradient-to-r from-violet-600 to-indigo-600 text-white ${
                            listenDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:from-violet-500 hover:to-indigo-500'
                          }`}
                        >
                          {listenLoading ? <LoadingSpinnerIcon /> : <ListenIcon />}
                          {listenLabel}
                        </button>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleShare}
                          className={mobileSecondaryActionClass}
                        >
                          <ShareIcon />
                          Share
                        </button>

                        <button
                          onClick={handleDownloadPdf}
                          disabled={isDownloadingPdf}
                          className={`${mobileSecondaryActionClass} ${isDownloadingPdf ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isDownloadingPdf ? <LoadingSpinnerIcon /> : <DownloadIcon />}
                          {isDownloadingPdf ? 'Generating...' : 'PDF'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Published Date */}
                    {formattedDate && (
                      <p className="mt-2 sm:mt-3 text-sm text-gray-500 dark:text-gray-400">
                        Published {formattedDate}
                      </p>
                    )}

                    {/* Action Buttons - Larger touch targets on mobile */}
                    <div className={actionLayoutClass}>
                      {onListen && (
                        <button
                          onClick={() => { void handleListenClick() }}
                          disabled={listenDisabled}
                          className={`${primaryActionClass} border border-transparent bg-gradient-to-r from-violet-600 to-indigo-600 text-white ${
                            listenDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:from-violet-500 hover:to-indigo-500'
                          }`}
                        >
                          {listenLoading ? <LoadingSpinnerIcon /> : <ListenIcon />}
                          {listenLabel}
                        </button>
                      )}

                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className={`
                          ${secondaryActionClass}
                          ${isSaved 
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400' 
                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                          }
                          ${isSaving ? 'opacity-50' : ''}
                        `}
                      >
                        <BookmarkIcon filled={isSaved} />
                        {isSaving ? '...' : isSaved ? 'Saved' : 'Save'}
                      </button>

                      <button
                        onClick={handleShare}
                        className={`${secondaryActionClass} border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700`}
                      >
                        <ShareIcon />
                        Share
                      </button>

                      <button
                        onClick={handleDownloadPdf}
                        disabled={isDownloadingPdf}
                        className={`${secondaryActionClass} border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 ${isDownloadingPdf ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {isDownloadingPdf ? <LoadingSpinnerIcon /> : <DownloadIcon />}
                        <span className="sm:hidden">{isDownloadingPdf ? '...' : 'PDF'}</span>
                        <span className="hidden sm:inline">{isDownloadingPdf ? 'Generating PDF...' : 'Download PDF'}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation - Clean, prominent with larger icons and text */}
        <div className="sticky top-0 z-40 mt-3 sm:mt-6 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur-sm">
          <div className="w-full px-0 sm:px-6 md:max-w-[1600px] md:mx-auto">
            <div className="bg-white dark:bg-gray-800 border-y border-gray-100 dark:border-gray-700 sm:border sm:rounded-2xl shadow-sm relative overflow-hidden">
              {/* Scroll hint gradient with arrow - right side */}
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white dark:from-gray-800 via-white/90 dark:via-gray-800/90 to-transparent pointer-events-none z-20 sm:hidden flex items-center justify-end pr-1">
                <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <nav ref={navRef} className="relative flex items-center gap-1 py-2.5 sm:py-3 px-2 sm:px-3 overflow-x-auto scrollbar-hide">
                {/* Sliding Indicator */}
                <div
                  className="absolute bottom-2.5 sm:bottom-3 h-12 sm:h-11 bg-gray-900 dark:bg-white rounded-xl transition-all duration-300 ease-out"
                  style={{
                    left: indicatorStyle.left,
                    width: indicatorStyle.width,
                  }}
                />
                
                {runtimeTabs.map((tabId) => {
                  const isActive = activeTab === tabId
                  return (
                    <button
                      key={tabId}
                      ref={(el) => { if (el) tabRefs.current.set(tabId, el) }}
                      onClick={() => {
                        if (activeTab === tabId) return // Already on this tab
                        setActiveTab(tabId)
                        // Scroll to top when changing tabs — use instant
                        // scroll after a delay so the new tab DOM has mounted
                        setTimeout(() => {
                          scrollRuntimeToTop(`tab-click:${tabId}`)
                        }, 100)
                      }}
                      className={`
                        relative z-10 flex items-center gap-2.5 px-4 py-3 text-base font-semibold rounded-xl whitespace-nowrap transition-all duration-200 active:scale-95
                        ${isActive
                          ? 'text-white dark:text-gray-900'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      <span className="w-6 h-6 [&>svg]:w-6 [&>svg]:h-6">{TAB_ICONS[tabId]}</span>
                      <span>{TAB_LABELS[tabId]}</span>
                    </button>
                  )
                })}
                {/* Extra padding for scroll hint */}
                <div className="w-8 flex-shrink-0 sm:hidden" />
              </nav>
            </div>
          </div>
        </div>

        {/* Tab Content - Full width on mobile, contained on desktop */}
        <main ref={tabContentRef} onClickCapture={handleContentClickCapture} className={isMobile ? mobileTabContentPaddingClass : 'pb-12'}>
          <div className="w-full px-3 sm:px-6 pt-4 sm:pt-6 md:max-w-[1600px] md:mx-auto">
            {renderTabContent()}
          </div>
        </main>

        {/* Evidence Drawer */}
        {evidenceDrawerOpen && (
          <EvidenceDrawer
            isOpen={evidenceDrawerOpen}
            onClose={() => setEvidenceDrawerOpen(false)}
            blockId={evidenceBlockId}
            sources={cig.sources}
            claims={cig.claims}
          />
        )}

        {shareUrl && (
          <ShareSheet
            isOpen={isShareSheetOpen}
            onClose={() => setIsShareSheetOpen(false)}
            title={displayTitle || 'Research Report'}
            text={shareText}
            url={shareUrl}
            resourceLabel={shareResourceLabel}
            onShareAction={handleShareAction}
          />
        )}
      </div>

      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </ScenarioProvider>
  )
}

function BookmarkIcon({ filled }: { filled?: boolean }) {
  return filled ? (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4a2 2 0 012-2h8a2 2 0 012 2v17l-7-4-7 4V4z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M6 4a2 2 0 012-2h8a2 2 0 012 2v17l-7-4-7 4V4z" />
    </svg>
  )
}

function ShareIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  )
}

function ListenIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M9 18V6l10-2v12M9 18a3 3 0 11-6 0 3 3 0 016 0zm10-2a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function LoadingSpinnerIcon() {
  return (
    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  )
}

function InfoPanel({ 
  cig, 
  studyMeta, 
  onPrivacyToggle,
  onNavigateToProfile 
}: { 
  cig: CIG
  studyMeta?: StudyMeta
  onPrivacyToggle?: (isPublic: boolean) => Promise<boolean>
  onNavigateToProfile?: (email: string) => void
}) {
  const [isChangingPrivacy, setIsChangingPrivacy] = useState(false)
  const [privacyStatus, setPrivacyStatus] = useState(studyMeta?.isPublic ?? false)
  
  // Sync privacy status when studyMeta changes
  useEffect(() => {
    setPrivacyStatus(studyMeta?.isPublic ?? false)
  }, [studyMeta?.isPublic])
  
  const created = studyMeta?.createdAt ? new Date(studyMeta.createdAt).toLocaleString() : '—'
  
  // Resolve avatar URL to absolute path
  const resolvedAvatarUrl = getConsistentAvatarUrl(
    studyMeta?.researcherAvatarUrl,
    studyMeta?.researcherEmail,
    studyMeta?.researcher
  )
  const privacyAnalyticsId =
    (typeof studyMeta?.researchId === 'number' && studyMeta.researchId > 0
      ? String(studyMeta.researchId)
      : undefined) ||
    studyMeta?.id ||
    studyMeta?.intelligenceReportId
  
  const handlePrivacyToggle = async () => {
    if (!onPrivacyToggle || isChangingPrivacy) return
    
    const newPrivacyStatus = !privacyStatus
    setIsChangingPrivacy(true)
    
    try {
      const success = await onPrivacyToggle(newPrivacyStatus)
      if (success) {
        setPrivacyStatus(newPrivacyStatus)
        if (privacyAnalyticsId) {
          analytics.trackResearchPrivacyToggled({
            research_id: String(privacyAnalyticsId),
            report_id: studyMeta?.intelligenceReportId || undefined,
            new_state: newPrivacyStatus ? 'public' : 'private',
          })
        }
      }
    } catch (error) {
      console.error('Privacy toggle failed:', error)
    } finally {
      setIsChangingPrivacy(false)
    }
  }
  
  const handleNavigateToProfile = () => {
    if (onNavigateToProfile && studyMeta?.researcherEmail) {
      onNavigateToProfile(studyMeta.researcherEmail)
    }
  }
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Research Information</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Key metadata for collaborators.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Researcher Section with Avatar and Link */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">Researcher</div>
          <button
            onClick={handleNavigateToProfile}
            disabled={!onNavigateToProfile || !studyMeta?.researcherEmail}
            className={`flex items-center gap-3 ${
              onNavigateToProfile && studyMeta?.researcherEmail 
                ? 'hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer' 
                : 'cursor-default'
            } rounded-lg p-2 -m-2 transition-colors w-full`}
          >
            <Avatar
              src={resolvedAvatarUrl}
              name={studyMeta?.researcher || 'Unknown Researcher'}
              size="md"
            />
            <div className="text-left">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {studyMeta?.researcher || 'Unknown Researcher'}
              </div>
            </div>
            {onNavigateToProfile && studyMeta?.researcherEmail && (
              <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>
        
        {/* Original Query */}
        <InfoRow label="Original Query" value={studyMeta?.query || cig.metadata.title} />
        
        {/* Created */}
        <InfoRow label="Created" value={created} />
        
        {/* Privacy Section with Toggle */}
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Privacy</div>
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${
              privacyStatus 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-gray-600 dark:text-gray-400'
            }`}>
              {privacyStatus ? 'Public' : 'Private'}
            </span>
            {onPrivacyToggle && (
              <button
                onClick={handlePrivacyToggle}
                disabled={isChangingPrivacy}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  privacyStatus ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                } ${isChangingPrivacy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-4 w-4 transform transition-transform bg-white rounded-full ${
                  privacyStatus ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            )}
          </div>
          {onPrivacyToggle && !privacyStatus && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Make public to share with others
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function InfoRow({ label, value, helper }: { label: string; value: string; helper?: string }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900 dark:text-white mt-1">{value}</div>
      {helper && <div className="text-xs text-gray-500 mt-0.5">{helper}</div>}
    </div>
  )
}

function EvidenceDrawer({ isOpen, onClose, blockId, sources, claims }: { isOpen: boolean; onClose: () => void; blockId: string | null; sources: CIG['sources']; claims: CIG['claims'] }) {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-gray-900 shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Evidence</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-4 overflow-y-auto h-[calc(100%-64px)]">
          <div className="space-y-4">
            {sources.map((source: any) => (
              <div key={source.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="font-medium text-gray-900 dark:text-white">{source.title}</div>
                {source.publisher && <div className="text-sm text-gray-500">{source.publisher}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CockpitRuntime
